import { useEffect, useState } from "react";
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, TextField, Typography,
} from "@mui/material";
import { useCategoryAdmin } from "./Blog.hooks";

/**
 * Admin CRUD over blog categories (client request 2026-07-27: the list used to
 * be hardcoded). Renaming moves every post that uses the old name; deleting
 * leaves those posts published but without a category.
 */
const CategoryManager = ({ open, onClose, onChanged }) => {
  const { categories, loading, load, createCategory, renameCategory, deleteCategory } = useCategoryAdmin();
  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) load(); }, [open, load]);

  const run = async (action) => {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await action();
      await load();
      onChanged?.();
    } catch (e) {
      setError(e.response?.data?.message || "Operation failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const onAdd = () => {
    const name = newName.trim();
    if (!name) return;
    run(async () => { await createCategory(name); setNewName(""); });
  };

  const onSaveRename = () => {
    const name = editName.trim();
    if (!name) return;
    run(async () => { await renameCategory(editingId, name); setEditingId(null); });
  };

  const onDelete = (category) => {
    const warning = category.postCount
      ? `Delete "${category.name}"? ${category.postCount} post(s) use it — they stay published but lose their category.`
      : `Delete "${category.name}"?`;
    if (!window.confirm(warning)) return;
    run(() => deleteCategory(category.id));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Blog Categories</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>}

        <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
          <TextField fullWidth size="small" placeholder="New category name…"
            value={newName} onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), onAdd())} />
          <Button variant="contained" onClick={onAdd} disabled={busy || !newName.trim()}>Add</Button>
        </Box>

        {loading && <Typography variant="body2">Loading…</Typography>}
        {!loading && categories.length === 0 && (
          <Typography variant="body2" color="text.secondary">No categories yet.</Typography>
        )}

        {categories.map((c) => (
          <Box key={c.id}
            sx={{ display: "flex", alignItems: "center", gap: 1, py: 1, borderBottom: "1px solid #EEE" }}>
            {editingId === c.id ? (
              <>
                <TextField fullWidth size="small" value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), onSaveRename())} />
                <Button size="small" variant="contained" onClick={onSaveRename} disabled={busy}>Save</Button>
                <Button size="small" onClick={() => setEditingId(null)}>Cancel</Button>
              </>
            ) : (
              <>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 500 }}>{c.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {c.postCount} post{c.postCount === 1 ? "" : "s"}
                  </Typography>
                </Box>
                <Button size="small" onClick={() => { setEditingId(c.id); setEditName(c.name); }}>
                  Rename
                </Button>
                <IconButton size="small" color="error" onClick={() => onDelete(c)} disabled={busy}>
                  ✕
                </IconButton>
              </>
            )}
          </Box>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CategoryManager;
